import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchJobs } from '../store/slices/jobsSlice';
import { generateTraceId, logUserAction } from '../utils/trace';

function DashboardPage() {
  const dispatch = useDispatch();
  const { user, activeTenant } = useSelector((state) => state.auth);
  const { jobs } = useSelector((state) => state.jobs);

  useEffect(() => {
    const traceId = generateTraceId();
    logUserAction(traceId, 'page_load', { page: 'dashboard' });
    dispatch(fetchJobs({ traceId }));
  }, [dispatch]);

  // Calculate job stats
  const stats = {
    total: jobs.length,
    running: jobs.filter(j => j.status === 'running' || j.status === 'pending').length,
    completed: jobs.filter(j => j.status === 'completed').length,
    failed: jobs.filter(j => j.status === 'failed').length,
  };

  const isAdmin = activeTenant?.role === 'admin' || user?.isGodMode;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6 text-gray-900" data-testid="dashboard-title">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* User Info Card */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold mb-4 text-gray-900">User Info</h2>
          <div className="space-y-2 text-gray-700">
            <p data-testid="dashboard-user-email">
              <span className="font-medium">Email:</span> {user?.email}
            </p>
            <p data-testid="dashboard-user-tenant">
              <span className="font-medium">Tenant:</span> {activeTenant?.name}
            </p>
            <p data-testid="dashboard-user-role">
              <span className="font-medium">Role:</span>{' '}
              <span className="capitalize">{activeTenant?.role}</span>
            </p>
            {user?.isGodMode && (
              <span className="inline-block bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium mt-2" data-testid="dashboard-god-mode-badge">
                God Mode
              </span>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold mb-4 text-gray-900">Job Stats</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900" data-testid="dashboard-stat-total">{stats.total}</div>
              <div className="text-sm text-gray-600">Total Jobs</div>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600" data-testid="dashboard-stat-running">{stats.running}</div>
              <div className="text-sm text-gray-600">Running</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600" data-testid="dashboard-stat-completed">{stats.completed}</div>
              <div className="text-sm text-gray-600">Completed</div>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600" data-testid="dashboard-stat-failed">{stats.failed}</div>
              <div className="text-sm text-gray-600">Failed</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-4">
        <Link
          to="/jobs"
          className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition font-medium"
          data-testid="dashboard-view-jobs-button"
        >
          View Jobs
        </Link>
        {isAdmin && (
          <Link
            to="/admin"
            className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition font-medium"
            data-testid="dashboard-admin-button"
          >
            Admin Panel
          </Link>
        )}
      </div>
    </div>
  );
}

export default DashboardPage;
